import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Box, Button, Grid, Typography, useTheme } from "@material-ui/core";
import Text from "../../../../../shared/components/Text";
import { convertToBase64 } from "../../../../../../utils/convertToBase64";

export const DocumentIdUploadCard = forwardRef(
  ({ documentSide, titleTid, onStateChange }, ref) => {
    const { palette, customBorderRadius } = useTheme();
    const inputRef = useRef(null);
    const [state, setState] = useState({
      hasPicture: false,
      capturedBase64: undefined,
      fileName: undefined,
      fileError: null,
    });

    const emit = useCallback(
      (next) => {
        setState(next);
        onStateChange?.(next);
      },
      [onStateChange]
    );

    const openPicker = useCallback(() => {
      inputRef.current?.click?.();
    }, []);

    const reset = useCallback(() => {
      if (inputRef.current) inputRef.current.value = "";
      emit({
        hasPicture: false,
        capturedBase64: undefined,
        fileName: undefined,
        fileError: null,
      });
    }, [emit]);

    useImperativeHandle(
      ref,
      () => ({
        openPicker,
        reset,
        getState: () => state,
      }),
      [openPicker, reset, state]
    );

    const onFileChange = useCallback(
      (e) => {
        const file = e?.target?.files?.[0];
        if (!file) return;
        const isAllowed =
          file.type === "image/jpeg" || file.type === "image/png";
        if (!isAllowed) {
          emit({
            hasPicture: false,
            capturedBase64: undefined,
            fileName: undefined,
            fileError: "INVALID_FILE_TYPE",
          });
          return;
        }
        convertToBase64(file, (result) => {
          emit({
            hasPicture: true,
            capturedBase64: result,
            fileName: file?.name,
            fileError: null,
          });
        });
      },
      [emit]
    );

    const showFormatHint = !state.capturedBase64;

    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="stretch"
        justifyContent="center"
        gridGap={12}
        py={2}
        width="100%"
      >
        <Grid
          container
          spacing={1}
          direction="row"
          alignItems="center"
          style={{
            boxShadow: "0px 0px 15px 2px rgba(0, 0, 0, 0.06)",
            borderRadius: customBorderRadius.paper,
            minHeight: 72,
          }}
        >
          {state.capturedBase64 ? (
            <>
              <Grid item xs={4} style={{ overflow: "hidden", padding: 8 }}>
                <img
                  alt={state.fileName || "uploaded"}
                  src={state.capturedBase64}
                  style={{
                    maxWidth: "100%",
                    maxHeight: 60,
                    objectFit: "contain",
                    display: "block",
                  }}
                />
              </Grid>
              <Grid
                item
                xs={6}
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  paddingLeft: 8,
                }}
              >
                <Typography variant="caption">{state.fileName}</Typography>
              </Grid>
              <Grid item xs={2} style={{ textAlign: "center" }}>
                <Button
                  style={{ minWidth: 36 }}
                  onClick={reset}
                  aria-label="delete"
                >
                  <i
                    className="material-icons"
                    style={{ color: palette.text.secondary }}
                  >
                    delete
                  </i>
                </Button>
              </Grid>
            </>
          ) : (
            <Grid item xs={12}>
              <Button
                fullWidth
                onClick={openPicker}
                style={{ borderRadius: customBorderRadius.paper, height: 72 }}
              >
                <Grid
                  container
                  item
                  xs={3}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  style={{
                    borderRight: `1px solid ${palette.grey[200]}`,
                    height: "50px",
                    paddingRight: customBorderRadius.paper,
                  }}
                >
                  <Grid item>
                    <i
                      className="material-icons"
                      style={{ color: palette.text.secondary }}
                    >
                      add_a_photo
                    </i>
                  </Grid>
                </Grid>
                <Grid
                  container
                  item
                  xs={9}
                  alignContent="center"
                  justifyContent="center"
                  style={{ display: "inline-block" }}
                >
                  <Grid item>
                    <Typography variant="body2">
                      <strong>
                        <Text tId={titleTid} />
                      </strong>
                    </Typography>
                  </Grid>
                  {showFormatHint && (
                    <Grid item>
                      <Typography
                        align="center"
                        variant="caption"
                        color="textSecondary"
                      >
                        <Text tId={"UPLOAD_PICTURE_FORMAT_HINT"} />
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Button>
            </Grid>
          )}
        </Grid>
        {state.fileError ? (
          <Typography
            align="center"
            variant="caption"
            style={{ color: palette.error.main }}
          >
            <Text tId="UPLOAD_PICTURE_FORMAT_ERROR" />
          </Typography>
        ) : null}

        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png"
          id={`id-doc-upload-${documentSide}`}
          hidden
          onChange={onFileChange}
        />
      </Box>
    );
  }
);
